/*
 * Single place to switch codec later (e.g. "h264" or "vp9").
 * After changing it, re-run the device test matrix.
 * The provider app must use the same value.
 */
export const VIDEO_CODEC = "vp8";

export const QUALITY_PRESETS = Object.freeze({
  // Auto: target 720p. No bitrateMin, so the SDK/browser can scale
  // resolution and bitrate down on a weak link instead of being forced to send more.
  auto: {
    width: 1280,
    height: 720,
    frameRate: 24,
    bitrateMax: 1200,
  },
  // Internal ladder rung only. Not shown to patients.
  stable: {
    width: 960,
    height: 540,
    frameRate: 24,
    bitrateMax: 800,
  },
  // Data Saver: capped at ~480p and ~400 kbps. Also receives the low stream.
  "data-save": {
    width: 640,
    height: 480,
    frameRate: 15,
    bitrateMax: 400,
  },
  // High Quality: target 1080p, up to 4000 kbps (about 900 MB per 30 min).
  // Prefers keeping resolution and dropping frame rate (optimizationMode "detail").
  detail: {
    width: 1920,
    height: 1080,
    frameRate: 30,
    bitrateMax: 4000,
  },
  // Kept for compatibility. Not exposed to patients.
  motion: {
    width: 1280,
    height: 720,
    frameRate: 60,
    bitrateMax: 2000,
  },
});

/* Sender-side auto ladder, best -> most frugal. */
export const AUTO_LADDER = ["auto", "stable", "data-save"];

/*
 * Manual modes remembered per device.
 * "auto" clears the memory; "detail"/"motion" are temporary and never saved.
 */
export const PERSISTABLE_MODES = Object.freeze(["data-save"]);

/* Low stream published in parallel when dual-stream is enabled (16:9). */
export const LOW_STREAM_PARAMS = Object.freeze({
  width: 640,
  height: 360,
  framerate: 15,
  bitrate: 200,
});

/*
 * Receiver fallback type for setStreamFallbackOption(uid, type):
 *   0 = off, 1 = low stream only, 2 = low stream, then audio-only.
 * We use 1: a soft 360p picture is better than a black screen in a
 * clinical call. Audio-only is a deliberate user choice, not an SDK decision.
 */
export const STREAM_FALLBACK_LOW_STREAM = 1;

/*
 * If true, the sender ladder turns the camera off automatically when the
 * lowest preset is still poor. Disabled after field tests showed it causes
 * avoidable blackouts.
 */
export const AUDIO_ONLY_FLOOR_ENABLED = false;

/* Opus mono. Audio is the last thing to degrade. */
export const AUDIO_ENCODER_DEFAULT = Object.freeze({
  sampleRate: 48000,
  stereo: false,
  bitrate: 32,
});

/* 24 kbps is only valid at 16/32 kHz. 48 kHz triggers SEND_AUDIO_BITRATE_TOO_LOW. */
export const AUDIO_ENCODER_DATA_SAVER = Object.freeze({
  sampleRate: 32000,
  stereo: false,
  bitrate: 24,
});